import React, { FC, useCallback, useEffect, useRef, useMemo } from 'react';
import { Box, IconButton, Paper, styled } from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Icon from 'react-eva-icons';
import { grey } from '@mui/material/colors';
import { Form, Formik, FormikErrors } from 'formik';
import {
  Button,
  useDialog,
  useSnackbar,
  Loader,
  Typography,
} from '@fdha/web-ui-library';
import {
  useAddNoteMutation,
  useDeleteNoteMutation,
  useUpdateNoteMutation,
} from '@fdha/graphql-api-admin';

import {
  NotesProps,
  removeDraftNote,
  removeEditNote,
} from '../../../../states/notesState';
import { useLocalStorage } from '../../../../hooks';
import { modules, createAutoSaveTimer } from '../../../../utils';

import Card from './Card';
import { buildFlaggedNoteRefetch, buildListNotesRefetch } from './constants';

interface NoteCardProps {
  note: NotesProps;
  patientId: string;
  index: number;
  isDraft?: boolean;
}

const Editor = styled(ReactQuill)(() => ({
  '& > div, & .ql-container.ql-snow': {
    border: 'none',
  },
  '& > div, & .ql-toolbar.ql-snow': {
    borderTop: 'none',
    borderRight: 'none',
    borderLeft: 'none',
  },
}));

const NoteCard: FC<NoteCardProps> = ({ note, patientId, index, isDraft }) => {
  const { showSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();
  const { saveToLocalStorage, removeFromLocalStorage } =
    useLocalStorage('notes');

  const [addNote] = useAddNoteMutation();
  const [updateNote] = useUpdateNoteMutation();
  const [deleteNote] = useDeleteNoteMutation();

  const initialValues = {
    name: note.name,
    content: note.content,
  };

  const saveDraft = useCallback(
    async (values: { name: string; content: string }) => {
      const data = {
        [patientId]: {
          index,
          ...note,
          ...values,
        },
      };
      await saveToLocalStorage(data);
      showSnackbar({
        message: 'Draft saved',
        severity: 'info',
      });
    },
    [patientId, index, note, saveToLocalStorage, showSnackbar]
  );

  // Store current form values in ref for auto-save interval
  const valuesRef = useRef(initialValues);
  // Store saveDraft in ref so timer doesn't need to be recreated when it changes
  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;

  // Create auto-save timer instance (only once)
  const autoSaveTimer = useMemo(
    () =>
      createAutoSaveTimer({
        onSave: () => {
          const currentValues = valuesRef.current;
          saveDraftRef.current(currentValues);
        },
        interval: 10000,
      }),
    [] // Empty deps - timer instance never recreated
  );

  // Start timer on mount, stop on unmount
  useEffect(() => {
    autoSaveTimer.startTimer();
    return () => {
      autoSaveTimer.stopTimer();
    };
  }, [autoSaveTimer]);

  const handleSubmit = async (values: { name: string; content: string }) => {
    try {
      autoSaveTimer.stopTimer();
      await saveDraft(values);
      if (note.id) {
        await updateNote({
          variables: { id: note.id, note: values },
          refetchQueries: note.flagged
            ? [
                buildListNotesRefetch(patientId),
                buildFlaggedNoteRefetch(patientId),
              ]
            : [buildListNotesRefetch(patientId)],
        });
        removeEditNote(note);
      } else {
        await addNote({
          variables: { note: values, patientId },
          refetchQueries: [buildListNotesRefetch(patientId)],
          awaitRefetchQueries: true,
        });
        removeDraftNote(note);
      }
      removeFromLocalStorage(patientId);
      showSuccessSnackbar('Note saved!');
    } catch (error) {
      autoSaveTimer.startTimer();
      console.error(error);
      showErrorSnackbar('Error editing/saving note');
    }
  };

  const handleCancel = () => {
    autoSaveTimer.stopTimer();
    if (note.id) {
      removeEditNote(note);
    } else {
      removeDraftNote(note);
    }
    removeFromLocalStorage(patientId);
    showSnackbar({ message: 'Changes not saved', severity: 'warning' });
  };

  const handleDelete = async () => {
    if (note.id) {
      openDialog({
        title:
          'Are you sure you want to delete this note? You will not be able to recover it.',
        content: null,
        confirmButtonLabel: 'Delete',
        cancelButtonLabel: 'Keep',
        handleConfirm: async () => {
          try {
            autoSaveTimer.stopTimer();
            removeEditNote(note);
            await deleteNote({
              variables: { id: note.id },
              refetchQueries: note.flagged
                ? [
                    buildListNotesRefetch(patientId),
                    buildFlaggedNoteRefetch(patientId),
                  ]
                : [buildListNotesRefetch(patientId)],
            });
            removeFromLocalStorage(patientId);
            showSuccessSnackbar('Note deleted');
          } catch (error) {
            autoSaveTimer.startTimer();
            showErrorSnackbar('Error deleting note');
          } finally {
            closeDialog();
          }
        },
      });
    } else {
      autoSaveTimer.stopTimer();
      removeDraftNote(note);
      removeFromLocalStorage(patientId);
    }
  };

  const showErrorSnackbar = (message: string) => {
    showSnackbar({ message: message, severity: 'error' });
  };

  const showSuccessSnackbar = (message: string) => {
    showSnackbar({ message: message, severity: 'success' });
  };

  const validate = (values: { name: string; content: string }) => {
    let errors: FormikErrors<{ name: string }> = {};

    if (!values.name.trim()) {
      errors.name = 'Note title is required';
      showErrorSnackbar(errors.name);
    }

    return errors;
  };

  return (
    <Paper>
      <Formik
        initialValues={initialValues}
        validateOnChange={false}
        validate={validate}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, isSubmitting }) => {
          // Update ref with current form values
          valuesRef.current = values;

          if (isSubmitting) {
            return (
              <Box
                position="relative"
                minHeight={200}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                padding={4}
              >
                <Loader />
                <Typography variant="body2" color="textSecondary" marginTop={2}>
                  Saving note...
                </Typography>
              </Box>
            );
          }

          return (
            <Form data-testid={`NOTE_CARD_FORM_${note.name}`}>
              <Card
                note={note}
                patientId={patientId}
                isEditing
                isDraft={isDraft}
                data-testid="NOTE_CARD_FORM"
              >
                <Editor
                  modules={modules}
                  value={values.content}
                  onChange={handleChange('content')}
                  preserveWhitespace
                />
              </Card>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                padding={2}
                data-testid={'EDIT_CARD'}
              >
                <IconButton onClick={handleDelete}>
                  <Icon name="trash-2-outline" size="large" fill={grey[600]} />
                </IconButton>
                <Box>
                  <Button onClick={handleCancel}>Cancel</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={isSubmitting}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Paper>
  );
};

export default NoteCard;
